import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { Asset } from '@shared/types';
import { useSidebarDrawer } from '@/components/AppShell/AppShell';
import styles from './AssetNav.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';
import { deliveryProgress } from '@/lib/deliveryProgress';

export type AssetStatus = 'pending' | 'ready' | 'uploaded';

export interface AssetNavItem {
  asset: Asset;
  index: number;
  status: AssetStatus | 'spinner';
  isCustom: boolean;
  /** Mindestens eine vollständige Version hochgeladen — zählt für den
   *  Fortschrittsbalken oben in der Sidebar (lib/deliveryProgress.ts). */
  delivered: boolean;
}

interface Props {
  items: AssetNavItem[];
  activeIndex: number;
  onSelect(index: number): void;
  allowCustomAssets: boolean;
  customAssetCount: number;
  maxCustomAssets: number;
  onAddCustomAsset(): void;
  onDeleteCustomAsset(assetId: string): void;
  /** Pulse every not-yet-uploaded asset (leave-page reminder — see
   *  hooks/useLeaveGuard). */
  remindUnuploaded?: boolean;
}

export function AssetNav({
  items,
  activeIndex,
  onSelect,
  allowCustomAssets,
  customAssetCount,
  maxCustomAssets,
  onAddCustomAsset,
  onDeleteCustomAsset,
  remindUnuploaded = false,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState({ top: false, bottom: false });
  const drawer = useSidebarDrawer();
  const handleSelect = (idx: number) => {
    onSelect(idx);
    drawer?.close();
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => {
      setArrows({
        top: el.scrollTop > 1,
        bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
      });
    };
    update();
    el.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [items.length]);

  const hasPredefined = items.some((it) => !it.isCustom);
  const firstCustomIndex = items.findIndex((it) => it.isCustom);
  const showCustomSeparator = hasPredefined && firstCustomIndex !== -1;
  const atMax = customAssetCount >= maxCustomAssets;
  // Fortschritt „Delivered 1 of 3" direkt unter dem Brand-Block (Philip,
  // 2026-10-03): auf einen Blick sehen, ob schon alles abgegeben ist.
  const progress = deliveryProgress(items);

  const handleDelete = (e: MouseEvent, asset: Asset) => {
    e.stopPropagation();
    // No window.confirm here — the parent (Generation/index.tsx) opens
    // DeleteAssetModal which handles confirmation, the local-only vs
    // cloud-cleanup choice, and the empty-asset shortcut path.
    onDeleteCustomAsset(asset.id);
  };

  return (
    <nav className={styles.nav} aria-label="Assets">
      {progress.total > 0 && (
        <div className={styles.progress} data-testid="delivery-progress">
          <div className={styles.progressRow}>
            <span>Delivered</span>
            <b>{progress.done} of {progress.total}</b>
          </div>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
            aria-label={`${progress.done} of ${progress.total} assets delivered`}
          >
            <div
              className={`${styles.progressFill} ${progress.ratio >= 1 ? styles.progressComplete : ''}`}
              style={{ width: `${Math.round(progress.ratio * 1000) / 10}%` }}
            />
          </div>
        </div>
      )}
      <div
        className={`${styles.scrollArrow} ${styles.scrollArrowTop} ${arrows.top ? '' : styles.hidden}`}
      >
        <i className="ph-bold ph-caret-up" />
      </div>
      <div ref={listRef} className={styles.list}>
        <div className={styles.sectionHeader}>Assets</div>
        {items.map((it, rowIdx) => {
          const isActive = it.index === activeIndex;
          const showSeparator =
            showCustomSeparator && rowIdx === firstCustomIndex;
          const remind =
            remindUnuploaded && it.status !== 'uploaded' && it.status !== 'spinner';
          return (
            <div key={it.asset.id}>
              {showSeparator && (
                <div className={styles.separator}>Custom</div>
              )}
              <button
                className={`${styles.item} ${isActive ? styles.active : ''} ${remind ? styles.remind : ''}`}
                onClick={() => handleSelect(it.index)}
                data-coach={`asset-${it.index}`}
              >
                <span
                  className={`${styles.activeMark} ${isActive ? '' : styles.hidden}`}
                  aria-hidden="true"
                >
                  <i className="ph-fill ph-caret-right" />
                </span>
                <div className={styles.body}>
                  <div className={styles.title} {...scriptAttrs(it.asset.title)}>
                    {it.asset.title || `Asset ${it.index + 1}`}
                  </div>
                </div>
                <StatusIcon status={it.status} />
                {it.isCustom && (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={(e) => handleDelete(e, it.asset)}
                    aria-label="Delete asset"
                    title="Delete asset"
                  >
                    <i className="ph-bold ph-trash" />
                  </button>
                )}
              </button>
            </div>
          );
        })}

        {allowCustomAssets && (
          <button
            type="button"
            className={`${styles.addBtn} ${atMax ? styles.addBtnDisabled : ''}`}
            onClick={() => { if (!atMax) onAddCustomAsset(); }}
            disabled={atMax}
          >
            {atMax ? (
              <>
                <i className="ph-bold ph-prohibit" />
                <span>Maximum reached</span>
              </>
            ) : (
              <>
                <i className="ph-bold ph-plus" />
                <span>New Asset</span>
              </>
            )}
          </button>
        )}
      </div>
      <div
        className={`${styles.scrollArrow} ${styles.scrollArrowBottom} ${arrows.bottom ? '' : styles.hidden}`}
      >
        <i className="ph-bold ph-caret-down" />
      </div>
    </nav>
  );
}

function StatusIcon({ status }: { status: AssetStatus | 'spinner' }) {
  if (status === 'uploaded') {
    return (
      <i
        className={`ph-fill ph-check-circle ${styles.status} ${styles.statusOk}`}
        aria-label="Uploaded"
      />
    );
  }
  if (status === 'spinner') {
    return (
      <i
        className={`ph-bold ph-spinner ${styles.status} ${styles.statusSpin}`}
        aria-label="Uploading"
      />
    );
  }
  if (status === 'ready') {
    return (
      <i
        className={`ph-fill ph-circle ${styles.statusReady}`}
        aria-label="Ready to upload"
      />
    );
  }
  return null;
}
